import { inject, Injectable } from '@angular/core';
import { finalize, map, Observable, of, shareReplay } from 'rxjs';
import { AuthService } from './auth.service';

/**
 * Serialises access-token refreshes (LBRD-6384).
 *
 * Every caller that needs a new token while a refresh is already in flight shares that one
 * request: they all get the same new token, or all get its error. The previous implementation
 * left waiters subscribed to a token that never arrived when the refresh failed, and could hand
 * them the token from an earlier refresh instead.
 */
@Injectable({
  providedIn: 'root'
})
export class SynchronizedTokenService {
  private readonly authService = inject(AuthService);

  private inFlight$: Observable<string> | null = null;

  refreshToken(): Observable<string> {
    if (!this.inFlight$) {
      this.inFlight$ = this.authService.refreshToken().pipe(
        map((authResult) => authResult.data.accessToken),
        // Runs once, when the refresh itself completes or fails -- not per subscriber.
        finalize(() => (this.inFlight$ = null)),
        // refCount: false -- a caller unsubscribing must not cancel a refresh the others still
        // wait on (and whose new tokens must be saved either way).
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.inFlight$;
  }

  /**
   * A new token for a request the API rejected with 401 while carrying `rejectedToken`. If
   * another request has already refreshed past it, the stored token is the answer -- no second
   * refresh; otherwise joins (or starts) the shared refresh.
   */
  refreshAfterUnauthorized(rejectedToken: string | null): Observable<string> {
    const current = this.authService.getAccessToken();
    if (!this.inFlight$ && current && current !== rejectedToken) {
      return of(current);
    }
    return this.refreshToken();
  }
}
